<template>
  <v-container fluid>
    <v-card class="pa-4">
      <!-- Header con botón Nueva Oficina -->
      <div class="d-flex align-center mb-4">
        <v-icon color="#ce501b" size="large" class="mr-2">mdi-office-building</v-icon>
        <h2 class="text-h5">Gestión de Oficinas / Áreas / Unidades</h2>
        <v-spacer></v-spacer>
        <v-btn
          color="#ce501b"
          prepend-icon="mdi-plus"
          @click="abrirModalNuevaOficina"
          elevation="2"
        >
          Nueva Oficina
        </v-btn>
        <v-btn
          icon
          @click="recargarDesdeInicio"
          :loading="loading && oficinas.length === 0"
          color="#ffe8e1"
          class="ml-2"
        >
          <v-icon color="#ce501b">mdi-refresh</v-icon>
        </v-btn>
      </div>

      <!-- Buscador -->
      <v-text-field
        ref="campoBusquedaRef"
        v-model="busqueda"
        prepend-inner-icon="mdi-magnify"
        label="Buscar oficina..."
        variant="outlined"
        density="comfortable"
        clearable
        class="mb-4"
        @input="filtrarOficinas"
      ></v-text-field>

      <!-- Tabla con infinite scroll -->
      <div 
        class="oficinas-container" 
        @scroll="handleScroll"
        ref="scrollContainer"
      >
        <!-- Tabla -->
        <v-table class="oficinas-table" fixed-header>
          <thead>
            <tr>
              <th class="text-center">Código</th>
              <th>Oficinas / Áreas / Unidades</th>
              <th class="text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="(oficina, index) in oficinasFiltradas"
              :key="index"
              class="oficina-row"
            >
              <!-- Código -->
              <td class="text-center">
                <v-chip color="primary" size="small" label>
                  {{ oficina.idOficina }}
                </v-chip>
              </td>

              <!-- Nombre de la Oficina -->
              <td>
                <div class="d-flex align-center">
                  <v-icon class="mr-2" size="small" color="blue-grey">mdi-office-building</v-icon>
                  <strong>{{ oficina.oficina }}</strong>
                </div>
              </td>

              <!-- Acciones -->
              <td class="text-center">
                <v-btn
                  icon
                  size="small"
                  color="green"
                  variant="text"
                  @click="verUsuarios(oficina)"
                  class="mr-1"
                >
                  <v-icon size="small">mdi-lock-open</v-icon>
                  <v-tooltip activator="parent" location="top">Ver Usuarios</v-tooltip>
                </v-btn>

                <v-btn
                  icon
                  size="small"
                  color="orange"
                  variant="text"
                  @click="editarOficina(oficina)"
                  class="mr-1"
                >
                  <v-icon size="small">mdi-pencil</v-icon>
                  <v-tooltip activator="parent" location="top">Editar</v-tooltip>
                </v-btn>

                <v-btn
                  icon
                  size="small"
                  color="red"
                  variant="text"
                  @click="confirmarEliminar(oficina)"
                >
                  <v-icon size="small">mdi-delete</v-icon>
                  <v-tooltip activator="parent" location="top">Eliminar</v-tooltip>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-table>

        <!-- Indicador de carga al final -->
        <div v-if="loading" class="text-center py-4">
          <v-progress-circular
            indeterminate
            color="primary"
            size="50"
          ></v-progress-circular>
          <p class="mt-2 text-caption">Cargando más oficinas...</p>
        </div>

        <!-- Mensaje cuando no hay más datos -->
        <div v-if="!loading && !hayMasDatos && oficinas.length > 0" class="text-center py-4 text-caption text-grey">
          <v-icon>mdi-check-circle</v-icon>
          No hay más oficinas para mostrar
        </div>

        <!-- Mensaje cuando no hay datos -->
        <div v-if="!loading && oficinas.length === 0" class="text-center py-8">
          <v-icon size="64" color="grey">mdi-office-building-outline</v-icon>
          <p class="text-h6 text-grey mt-2">No hay oficinas registradas</p>
        </div>
      </div>
    </v-card>

    <!-- Modal Nueva/Editar Oficina -->
    <v-dialog v-model="dialogNuevaOficina" max-width="500px" persistent>
      <v-card>
        <v-card-title class="text-h6 text-white" style="background-color: #004d82;">
          {{ modoEdicion ? 'Editar Oficina' : 'Nueva Oficina / Área / Unidad' }}
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="cerrarModal"
            size="small"
            variant="text"
            color="white"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-6">
          <v-form ref="formNuevaOficina" v-model="validFormNuevo" @submit.prevent>
            <!-- Nombre de la Oficina -->
            <v-text-field
              ref="campOficinaRef"
              v-model="nuevaOficina.oficina"
              label="Oficina / Área / Unidad"
              prepend-icon="mdi-office-building"
              variant="outlined"
              density="comfortable"
              :rules="[v => !!v || 'Nombre de la oficina es requerido']"
              class="mb-3 campo-amarillo"
              @keydown="handleKeyOficina"
            ></v-text-field>
          </v-form>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
        
          <v-btn
            type="button"
            color="#ce501b"
            variant="elevated"
            @click="guardarOficina"
            :loading="guardando"
            :disabled="!validFormNuevo"
          >
            {{ modoEdicion ? 'Actualizar' : 'Grabar' }}
          </v-btn>

          <v-btn
            type="button"
            color="#9B9B9B"
            variant="text"
            @click="cerrarModal"
          >
            Cancelar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal Ver Usuarios de la Oficina -->
    <v-dialog v-model="dialogVerUsuarios" max-width="600px" persistent>
      <v-card>
        <v-card-title class="text-h6 text-white" style="background-color: #004d82;">
          <v-icon class="mr-2">mdi-account-multiple</v-icon>
          Usuarios de la oficina
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="cerrarModalUsuarios"
            size="small"
            variant="text"
            color="white"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4 pb-2">
          <div v-if="oficinaSeleccionada" class="mb-3">
            <v-chip color="primary" label size="small" class="mr-2">
              <v-icon start size="small">mdi-office-building</v-icon>
              {{ oficinaSeleccionada.oficina }}
            </v-chip>
            <span class="text-caption text-grey" v-if="!loadingUsuariosModal && todosLosUsuariosOficina.length > 0">
              Total: {{ todosLosUsuariosOficina.length }} usuario(s)
            </span>
          </div>

          <!-- Lista de usuarios con infinite scroll -->
          <div 
            class="usuarios-scroll-container"
            @scroll="handleScrollUsuarios"
            ref="scrollContainerUsuarios"
          >
            <!-- Lista simple de usuarios -->
            <div v-if="usuariosOficina.length > 0">
              <div
                v-for="(usuario, index) in usuariosOficina"
                :key="index"
                class="usuario-list-item"
              >
                {{ usuario.usuario }}
              </div>
              
              <!-- Indicador de carga al final -->
              <div v-if="loadingUsuariosModal" class="text-center py-3">
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="30"
                ></v-progress-circular>
                <p class="mt-2 text-caption">Cargando más usuarios...</p>
              </div>
              
              <!-- Mensaje cuando no hay más datos -->
              <div v-if="!loadingUsuariosModal && !hayMasUsuarios" class="text-center py-3 text-caption text-grey">
                <v-icon size="small">mdi-check-circle</v-icon>
                No hay más usuarios para mostrar
              </div>
            </div>

            <!-- Loading inicial -->
            <div v-else-if="loadingUsuariosModal" class="text-center py-8">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <p class="mt-2 text-caption">Cargando usuarios...</p>
            </div>

            <!-- Sin usuarios -->
            <div v-else class="text-center py-8">
              <v-icon size="64" color="grey">mdi-account-off</v-icon>
              <p class="text-body-2 text-grey mt-2">No hay usuarios en esta oficina</p>
            </div>
          </div>

          <!-- Contador de usuarios mostrados -->
          <div v-if="usuariosOficina.length > 0" class="text-center mt-2 text-caption text-grey">
            Mostrando registros del 1 al {{ usuariosOficina.length }} de un total de {{ todosLosUsuariosOficina.length }} registros
          </div>
        </v-card-text>

        <v-card-actions class="px-4 pb-3">
          <v-spacer></v-spacer>
          <v-btn
            color="#ce501b"
            variant="elevated"
            @click="cerrarModalUsuarios"
            size="small"
          >
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, nextTick } from 'vue'
//import axios from 'axios'
import axios from '@/plugins/axios'
import Swal from 'sweetalert2'

const oficinas = ref([])
const loading = ref(false)
const hayMasDatos = ref(true)
const paginaActual = ref(1)
const itemsPorPagina = ref(20)
const scrollContainer = ref(null)
const busqueda = ref('')
const dialogNuevaOficina = ref(false)
const dialogVerUsuarios = ref(false)
const todasLasOficinas = ref([])
const guardando = ref(false)
const validFormNuevo = ref(false)
const formNuevaOficina = ref(null)
const modoEdicion = ref(false)
const oficinaEditando = ref(null)
const oficinaSeleccionada = ref(null)

// Variables para usuarios de la oficina en modal Ver Usuarios
const usuariosOficina = ref([])
const todosLosUsuariosOficina = ref([])
const loadingUsuariosModal = ref(false)
const hayMasUsuarios = ref(true)
const paginaActualUsuarios = ref(1)
const itemsPorPaginaUsuarios = ref(15)
const scrollContainerUsuarios = ref(null)

const campOficinaRef = ref(null)
const campoBusquedaRef = ref(null)

// Datos de la nueva oficina
const nuevaOficina = ref({
  oficina: ''
})

onMounted(async () => {
  await cargarOficinas()
})

const cargarOficinas = async () => {
  if (loading.value || !hayMasDatos.value) return
  
  loading.value = true
  
  try {
    if (todasLasOficinas.value.length === 0) {
      const response = await axios.get('/api/Oficinas/list-oficinas')
      
      if (response.data.success) {
        todasLasOficinas.value = response.data.data
        //console.log('Oficinas cargadas:', todasLasOficinas.value.length)     //Usarlo solo para ver los errores en el testeo de Software
      } else {
        Swal.fire({
          icon: 'error',
          iconColor: '#ce501b',
          title: 'Error',
          text: response.data.mensaje || 'No se pudieron cargar las oficinas',
          customClass: {
            title: 'swal-title-custom',
            container: 'swal-sobre-modal'
          }
        })
        return
      }
    }
    
    const inicio = (paginaActual.value - 1) * itemsPorPagina.value
    const fin = inicio + itemsPorPagina.value
    const itemsPaginados = todasLasOficinas.value.slice(inicio, fin)
    
    if (itemsPaginados.length > 0) {
      oficinas.value = [...oficinas.value, ...itemsPaginados]
      paginaActual.value++
    }
    
    if (fin >= todasLasOficinas.value.length) {
      hayMasDatos.value = false
    }
    
  } catch (error) {
    //console.error('Error al cargar oficinas:', error)    //Usarlo solo para ver los errores en el testeo de Software
    Swal.fire({
      icon: 'error',
      iconColor: '#ce501b',
      title: 'Error',
      text: 'Hubo un problema al cargar las oficinas',
      customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal'
        }
    })
  } finally {
    loading.value = false
  }
}

const handleScroll = (event) => {
  const { scrollTop, scrollHeight, clientHeight } = event.target
  if (scrollTop + clientHeight >= scrollHeight * 0.8) {
    cargarOficinas()
  }
}

const recargarDesdeInicio = () => {
  oficinas.value = []
  todasLasOficinas.value = []
  paginaActual.value = 1
  hayMasDatos.value = true
  cargarOficinas()
}

const oficinasFiltradas = computed(() => {
  if (!busqueda.value) {
    return oficinas.value
  }
  
  const termino = busqueda.value.toLowerCase()
  return oficinas.value.filter(oficina => 
    oficina.idOficina?.toString().includes(termino) ||
    oficina.oficina?.toLowerCase().includes(termino)
  )
})

const filtrarOficinas = () => {
  // Filtrado automático con computed
}

const abrirModalNuevaOficina = () => {
  modoEdicion.value = false
  limpiarFormulario()
  dialogNuevaOficina.value = true

    nextTick(() => {
    setTimeout(() => {
      const input = campOficinaRef.value?.$el?.querySelector('input')
      if (input) input.focus()
    }, 100)
  })

}

const editarOficina = async (oficina) => {
  //console.log('Editar oficina:', oficina)     //Usarlo solo para ver los errores en el testeo de Software
  modoEdicion.value = true
  oficinaEditando.value = oficina
  
  try {
    // Cargar detalle de la oficina desde el API
    const response = await axios.get(`/api/Oficinas/detalle/${oficina.idOficina}`)
    
    if (response.data.success) {
      const detalle = response.data.data
      //console.log('Detalle de la oficina:', detalle)  //Usarlo solo para ver los errores en el testeo de Software
      
      nuevaOficina.value = {
        oficina: detalle.nombreOficina
      }
      
      dialogNuevaOficina.value = true

       // ✅ Enfocar el campo al abrir el modal de edición
      nextTick(() => {
        setTimeout(() => {
          const input = campOficinaRef.value?.$el?.querySelector('input')
          if (input) input.focus()
        }, 100)
      })

    } else {
      Swal.fire({
        icon: 'error',
        iconColor: '#ce501b',
        title: 'Error',
        text: response.data.mensaje || 'No se pudo cargar la oficina',
        customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal'
        }
      })
    }
  } catch (error) {
    //console.error('Error al cargar detalle de la oficina:', error)  //Usarlo solo para ver los errores en el testeo de Software
    Swal.fire({
      icon: 'error',
      iconColor: '#ce501b',
      title: 'Error',
      text: 'Hubo un problema al cargar la oficina',
      customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal'
        }
    })
  }
}


const handleKeyOficina = (event) => {
  if (event.altKey && event.code === 'KeyG') {
    event.preventDefault()
    guardarOficina()
  }
}

const guardarOficina = async () => {
 
  const { valid } = await formNuevaOficina.value.validate()
  
  if (!valid) {
    return
  }

  guardando.value = true

  try {
    if (modoEdicion.value) {
      // ========================================
      // MODO EDICIÓN - Actualizar oficina
      // ========================================
      const idUsuarioSesion = localStorage.getItem('idUsuario')
      
      if (!idUsuarioSesion) {
        Swal.fire({
          icon: 'error',
          iconColor: '#ce501b',
          title: 'Error',
          text: 'No se pudo obtener la información del usuario en sesión',
          customClass: {
            title: 'swal-title-custom',
            container: 'swal-sobre-modal'
          }
        })
        guardando.value = false
        return
      }

      const payload = {
        idOficina: oficinaEditando.value.idOficina,
        nombreOficina: nuevaOficina.value.oficina
      }

      //console.log('Actualizando oficina:', payload)     //Usarlo solo para ver los errores en el testeo de Software

      const response = await axios.put(
        `/api/Oficinas/actualizar-oficina?idUsuarioSesion=${idUsuarioSesion}`,
        payload
      )

      if (response.data.success) {
        cerrarModal()
        
        await Swal.fire({
          icon: 'success',
          iconColor: '#ce501b',
          title: '¡Éxito!',
          text: 'Oficina actualizada correctamente',
          timer: 2500,
          confirmButtonColor: '#ce501b',
          customClass: {
            title: 'swal-title-custom',
            container: 'swal-sobre-modal',
            confirmButton: 'swal-btn-confirmar'
          },
          showConfirmButton: true
        })

        recargarDesdeInicio()
      } else {
        Swal.fire({
          icon: 'error',
          iconColor: '#ce501b',
          title: 'Error',
          text: response.data.mensaje || 'No se pudo actualizar la oficina',
          customClass: {
            title: 'swal-title-custom',
            container: 'swal-sobre-modal'
          }
        })
      }
    } else {
      // ========================================
      // MODO NUEVO - Crear oficina
      // ========================================
      const payload = {
        descripcion: nuevaOficina.value.oficina
      }

      //console.log('Creando oficina:', payload)    //Usarlo solo para ver los errores en el testeo de Software

      const response = await axios.post(
        '/api/Oficinas/registrar-oficina',
        payload
      )

      //console.log('Respuesta recibida:', response.data)    //Usarlo solo para ver los errores en el testeo de Software

      if (response.data.success) {
        cerrarModal()
        
        await Swal.fire({
          icon: 'success',
          title: '¡Éxito!',
          iconColor: '#ce501b',
          text: 'Oficina registrada correctamente',
          timer: 2500,
          confirmButtonColor: '#ce501b',
          customClass: {
            title: 'swal-title-custom',
            confirmButton: 'swal-btn-confirmar'
          },
          showConfirmButton: true
        })

        recargarDesdeInicio()
      } else {
        Swal.fire({
          icon: 'error',
          iconColor: '#ce501b',
          title: 'Error',
          text: response.data.mensaje || 'No se pudo registrar la oficina',
          customClass: {
            title: 'swal-title-custom',
            container: 'swal-sobre-modal'
          }
        })
      }
    }
  } catch (error) {
    //console.error('Error al guardar oficina:', error)   //Usarlo solo para ver los errores en el testeo de Software
    Swal.fire({
      icon: 'error',
      iconColor: '#ce501b',
      title: 'Error',
      text: error.response?.data?.mensaje || 'Hubo un problema al guardar la oficina',
      customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal'
        }
    })
  } finally {
    guardando.value = false
  }
}


const limpiarFormulario = () => {
  nuevaOficina.value = {
    oficina: ''
  }
  if (formNuevaOficina.value) {
    formNuevaOficina.value.reset()
  }
}

const cerrarModal = () => {
  dialogNuevaOficina.value = false
  modoEdicion.value = false
  oficinaEditando.value = null
  limpiarFormulario()
}


// Método para cargar usuarios de la oficina con infinite scroll
const cargarUsuariosOficina = async (idOficina) => {
  if (loadingUsuariosModal.value || !hayMasUsuarios.value) return
  
  loadingUsuariosModal.value = true
  
  try {
    // Solo cargar una vez todos los datos del API
    if (todosLosUsuariosOficina.value.length === 0) {
      const idUsuarioSesion = localStorage.getItem('idUsuario')
      
      if (!idUsuarioSesion) {
        Swal.fire({
          icon: 'error',
          iconColor: '#ce501b',
          title: 'Error',
          text: 'No se pudo obtener la información del usuario en sesión',
          customClass: {
            title: 'swal-title-custom',
            container: 'swal-sobre-modal'
         }
        })
        loadingUsuariosModal.value = false
        return
      }

      const response = await axios.get(
        `/api/Oficinas/usuarios/${idOficina}?idUsuarioSesion=${idUsuarioSesion}`
      )

      if (response.data.success) {
        todosLosUsuariosOficina.value = response.data.data
        //console.log('Usuarios de la oficina cargados:', todosLosUsuariosOficina.value.length)   //Usarlo solo para ver los errores en el testeo de Software
      } else {
        Swal.fire({
          icon: 'error',
          iconColor: '#ce501b',
          title: 'Error',
          text: response.data.mensaje || 'No se pudieron cargar los usuarios',
          customClass: {
            title: 'swal-title-custom',
            container: 'swal-sobre-modal'
          }
        })
        loadingUsuariosModal.value = false
        return
      }
    }
    
    // Simular paginación del lado del cliente
    const inicio = (paginaActualUsuarios.value - 1) * itemsPorPaginaUsuarios.value
    const fin = inicio + itemsPorPaginaUsuarios.value
    const itemsPaginados = todosLosUsuariosOficina.value.slice(inicio, fin)
    
    if (itemsPaginados.length > 0) {
      usuariosOficina.value = [...usuariosOficina.value, ...itemsPaginados]
      paginaActualUsuarios.value++
    }
    
    // Verificar si hay más datos
    if (fin >= todosLosUsuariosOficina.value.length) {
      hayMasUsuarios.value = false
    }
    
  } catch (error) {
    //console.error('Error al cargar usuarios de la oficina:', error)   //Usarlo solo para ver los errores en el testeo de Software
    Swal.fire({
      icon: 'error',
      iconColor: '#ce501b',
      title: 'Error',
      text: 'Hubo un problema al cargar los usuarios de la oficina',
      customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal'
        }
    })
  } finally {
    loadingUsuariosModal.value = false
  }
}



const verUsuarios = async (oficina) => {
  //console.log('Ver usuarios de la oficina:', oficina)      //Usarlo solo para ver los errores en el testeo de Software
  oficinaSeleccionada.value = oficina
  dialogVerUsuarios.value = true
  
  // Resetear estado
  usuariosOficina.value = []
  todosLosUsuariosOficina.value = []
  paginaActualUsuarios.value = 1
  hayMasUsuarios.value = true
  
  await cargarUsuariosOficina(oficina.idOficina)
}

const handleScrollUsuarios = (event) => {
  const { scrollTop, scrollHeight, clientHeight } = event.target
  
  // Si llegamos al 80% del scroll, cargar más
  if (scrollTop + clientHeight >= scrollHeight * 0.8) {
    cargarUsuariosOficina(oficinaSeleccionada.value?.idOficina)
  }
}

const cerrarModalUsuarios = () => {
  dialogVerUsuarios.value = false
  oficinaSeleccionada.value = null
  usuariosOficina.value = []
  todosLosUsuariosOficina.value = []
  paginaActualUsuarios.value = 1
  hayMasUsuarios.value = true
}

const confirmarEliminar = async (oficina) => {
  const result = await Swal.fire({
    title: '¿Estás seguro?',
    text: `¿Deseas eliminar la oficina "${oficina.oficina}"?`,
    icon: 'warning',
    iconColor: '#ce501b',
    showCancelButton: true,
    confirmButtonColor: '#ce501b',
    cancelButtonColor: '#9B9B9B',
    confirmButtonText: 'Sí, eliminar',
    customClass: {
          title: 'swal-title-custom',
          confirmButton: 'swal-btn-confirmar',
          cancelButton: 'swal-btn-cancelar'
        },
    cancelButtonText: 'Cancelar'
  })

  if (result.isConfirmed) {
    eliminarOficina(oficina)
  }
}



const eliminarOficina = async (oficina) => {
  //console.log('Eliminar oficina:', oficina)   //Usarlo solo para ver los errores en el testeo de Software
  
  try {
    const idUsuarioSesion = localStorage.getItem('idUsuario')
    
    if (!idUsuarioSesion) {
      Swal.fire({
        icon: 'error',
        iconColor: '#ce501b',
        title: 'Error',
        text: 'No se pudo obtener la información del usuario en sesión',
        customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal'
        }
      })
      return
    }

    // Llamar al endpoint DELETE
    const response = await axios.delete(
      `/api/Oficinas/eliminar-oficina/${oficina.idOficina}?idUsuarioSesion=${idUsuarioSesion}`
    )

    //console.log('Respuesta eliminación:', response.data)      //Usarlo solo para ver los errores en el testeo de Software

    if (response.data.success) {
      await Swal.fire({
        icon: 'success',
        iconColor: '#ce501b',
        title: '¡Eliminado!',
        text: `La oficina "${oficina.oficina}" ha sido eliminada exitosamente.`,
        confirmButtonColor: '#ce501b',
         customClass: {
          title: 'swal-title-custom',
          confirmButton: 'swal-btn-confirmar'
        },

        timer: 2500,
        showConfirmButton: true
      })

      // Recargar la lista de oficinas
      recargarDesdeInicio()
    } else {
      Swal.fire({
        icon: 'error',
        iconColor: '#ce501b',
        title: 'Error',
        text: response.data.mensaje || 'No se pudo eliminar la oficina',
        customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal'
        }
      })
    }
  } catch (error) {
    //console.error('Error al eliminar oficina:', error)  //Usarlo solo para ver los errores en el testeo de Software
    
    // Mensaje específico si hay usuarios asociados
    if (error.response?.status === 400) {
      Swal.fire({
        icon: 'warning',
        iconColor: '#ce501b',
        title: 'No se puede eliminar',
        text: error.response?.data?.mensaje || 'Esta oficina contiene usuarios asociados',
        customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal'
        }
      })
    } else {
      Swal.fire({
        icon: 'error',
        iconColor: '#ce501b',
        title: 'Error',
        text: 'Hubo un problema al eliminar la oficina',
        customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal'
        }
      })
    }
  }
}


const handleKeyboardGlobal = (event) => {
  // Alt + N → abrir modal Nueva Oficina
  if (event.altKey && event.code === 'KeyN') {
    event.preventDefault()
    if (!dialogNuevaOficina.value) {
      abrirModalNuevaOficina()
    }
  }

  // Alt + B → enfocar buscador
  if (event.altKey && event.code === 'KeyB') {
    event.preventDefault()
    if (!dialogNuevaOficina.value && !dialogVerUsuarios.value) {
      const input = campoBusquedaRef.value?.$el?.querySelector('input')
      if (input) input.focus()
    }
  }

  // ESC → cerrar modal si está abierto
  if (event.code === 'KeyEscape') {
    if (dialogNuevaOficina.value) {
      event.preventDefault()
      cerrarModal()
    }
    if (dialogVerUsuarios.value) {
      event.preventDefault()
      cerrarModalUsuarios()
    }
  }
}

onMounted(async () => {
  await cargarOficinas()
  document.addEventListener('keydown', handleKeyboardGlobal)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyboardGlobal)
})



</script>

<style scoped>
.oficinas-container {
  max-height: calc(100vh - 320px);
  overflow-y: auto;
  padding-right: 8px;
}

.oficinas-container::-webkit-scrollbar {
  width: 8px;
}

.oficinas-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.oficinas-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.oficinas-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.oficinas-table {
  background-color: transparent;
}

.oficinas-table th {
  background-color: #def2ff !important;
  font-weight: bold !important;
  color: #37495f !important;
  white-space: nowrap;
}

.oficina-row {
  transition: background-color 0.2s ease;
}

.oficina-row:hover {
  background-color: rgba(59, 130, 200, 0.05);
}

.oficinas-table td {
  padding: 12px 16px !important;
}

/* Campos amarillos */
:deep(.campo-amarillo .v-field) {
  background-color: #FFFACD !important;
}

/* Estilos para el modal de usuarios */
.usuarios-scroll-container {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: #fafafa;
}

.usuarios-scroll-container::-webkit-scrollbar {
  width: 8px;
}

.usuarios-scroll-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.usuarios-scroll-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.usuarios-scroll-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.usuario-list-item {
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  background-color: white;
  transition: background-color 0.2s ease;
  font-size: 14px;
  color: #333;
}

.usuario-list-item:hover {
  background-color: #f5f5f5;
}

.usuario-list-item:last-child {
  border-bottom: none;
}

.usuario-list-item:nth-child(even) {
  background-color: #fafafa;
}

.usuario-list-item:nth-child(even):hover {
  background-color: #f0f0f0;
}
</style>


<style>

.swal-title-custom {
  color: #004d82 !important;
  font-size: 1.3rem !important;
}

.swal-text-custom {
  color: #555555 !important;
  font-size: 0.95rem !important;
}

.swal-btn-custom {
  background-color: #ce501b !important;
  color: #ffffff !important;
  border-radius: 6px !important;
  padding: 8px 24px !important;
}

.swal-btn-custom:hover {
  background-color: #a83d14 !important;
}

.swal-btn-confirmar {
  color: white !important;
}

</style>

<style>
.swal-sobre-modal {
  z-index: 10000 !important;
}
</style>